import { SeverityLevel } from '@microsoft/applicationinsights-web';
import React, { PureComponent, useState, useCallback, useContext, type ErrorInfo, type ReactNode } from 'react';
import { appInsights } from '../../lib/appInsights';

interface AdditionalInfo {
  [key: string]: string | number | boolean | AdditionalInfo;
}

interface ErrorWithConstraint extends Error {
  constraint: string;
}

export type Cause = string | Error;
export type ErrorOptions = {
  cause?: Cause;
  additionalInfo?: AdditionalInfo;
};

export class AppError extends Error {
  cause: Cause | undefined;
  additionalInfo: AdditionalInfo | undefined;

  constructor(error: string | Error, options?: ErrorOptions) {
    const errorMessage = error instanceof Error ? error.message : error;
    super(errorMessage);
    if (error instanceof Error) {
      this.name = error.name;
      if (error.stack) this.stack = error.stack;

      if (error.name === 'OverconstrainedError') {
        this.message = `${error.name}: constraint - ${(error as ErrorWithConstraint).constraint}`;
      }
    }
    if (options) {
      this.cause = options?.cause;
      this.additionalInfo = options.additionalInfo;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BoundaryStore = { [key: string]: any };
const BoundaryContext = React.createContext<BoundaryStore | null>(null);

export type ErrorBoundaryError = Error | AppError | undefined;

export interface RenderErrorProps {
  error: ErrorBoundaryError;
  boundaryStore: BoundaryStore;
  resetErrorBoundary: () => void;
}

interface ErrorBoundaryProps {
  name?: string;
  onDidCatch?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  renderError?: ({ error, boundaryStore, resetErrorBoundary }: RenderErrorProps) => ReactNode;
  error?: ErrorBoundaryError;
  children: ReactNode | undefined;
}

interface ErrorBoundaryState {
  error: ErrorBoundaryError;
}

export class ErrorBoundary extends PureComponent<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    error: undefined,
  };

  // boundaryStore is a place to save anything that might be needed
  // by a custom error boundary to unmount or tear down any sort of
  // state, session, or SDK that was initialized in the children below it
  // These would typicaly include functions or SDK objects
  private boundaryStore: React.MutableRefObject<null | BoundaryStore> = React.createRef();

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  // Handle error via lifecycle method and call onDidCatch
  // Note: For some reason this doesn't fire unless a fallback error
  // UI is provided via the renderError prop
  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onDidCatch && this.props.onDidCatch(error, errorInfo);
  }

  private _reset(): void {
    this.props.onReset && this.props.onReset();
    this.setState({
      error: undefined,
    });
  }

  public render(): ReactNode {
    const boundaryName = this.props.name || 'DefaultErrorBoundary';
    // error can be caught from the children below the boundary or be
    // passed in a prop from the boundary's parent.  In this latter case,
    // the error boundary is considered a controlled component.
    const error = this.props.error || this.state.error;
    const { children, renderError } = this.props;
    this.boundaryStore.current = this.boundaryStore.current || {};
    let renderedChildren = children;

    if (error) {
      appInsights.trackException({
        error,
        severityLevel: SeverityLevel.Error,
        properties: {
          sourceComponent: boundaryName,
          ...(error as AppError).additionalInfo,
        },
      });

      if (renderError) {
        // Prevent rendering of children that caused the error,
        // render fallback error UI instead
        renderedChildren = renderError({
          ...this.props,
          error,
          boundaryStore: this.boundaryStore.current,
          resetErrorBoundary: this._reset.bind(this),
        });
      }
    }

    return <BoundaryContext.Provider value={this.boundaryStore.current}>{renderedChildren}</BoundaryContext.Provider>;
  }
}

export const useErrorBoundary = (): {
  boundaryStore: BoundaryStore;
  throwError: (error: Error) => void;
} => {
  const boundaryStore = useContext(BoundaryContext) || {};

  // You can use this to throw an asynchronous error within the React lifecycle
  // so that React error boundaries will catch it.  This works because setState
  // callbacks run during the normal React lifecycle
  const [, setError] = useState();
  const throwError = useCallback(
    (error: Error) => {
      setError(() => {
        throw error;
      });
    },
    [setError]
  );

  return { boundaryStore, throwError };
};
